import { Slot } from "@/components/ui/slot";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/utils/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";

/**
 * The button hierarchy, in the order a screen should reach for it.
 *
 * - `primary`   the one thing this screen is for. At most one per view.
 * - `secondary` supporting actions that sit beside the primary one.
 * - `tertiary`  a lightweight action inside a row, a toolbar, a card header.
 * - `ghost`     an action that should read as text until you point at it.
 * - `danger`    destructive, and always behind a confirmation.
 * - `warning`   a change worth confirming that takes nothing away.
 * - `link`      an action that behaves like prose.
 *
 * Two things are deliberate. There is no filled secondary: putting two solid
 * buttons side by side leaves the eye no answer about which one to press. And
 * every variant presses — a 1% scale down on `:active` — because a control that
 * does not move under the pointer does not feel connected to the thing it does.
 */
const buttonVariants = cva(
  [
    "relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap",
    "rounded-field font-medium",
    "transition-[background-color,border-color,color,box-shadow,transform,opacity]",
    "duration-[var(--duration-fast)] ease-[var(--ease-out-soft)]",
    "active:scale-[0.985] active:duration-75",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]",
    "disabled:pointer-events-none disabled:opacity-40",
    // `aria-disabled` rather than `disabled` where the button has to say *why*
    // it is held back: a disabled control fires no hover and takes no focus,
    // so the explanation would be unreachable by pointer and keyboard alike.
    "aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:shadow-none",
    "aria-disabled:active:scale-100",
    "aria-busy:pointer-events-none",
    "[&_svg]:size-4 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: "bg-accent text-accent-fg shadow-[var(--shadow-lift)] hover:bg-accent-hover",
        secondary:
          "border border-line-strong bg-surface-raised text-fg shadow-[var(--shadow-lift)] hover:bg-surface-sunken",
        /** Kept as the name most of the app already writes; same thing as `secondary`. */
        outline:
          "border border-line-strong bg-surface-raised text-fg shadow-[var(--shadow-lift)] hover:bg-surface-sunken",
        tertiary: "bg-surface-inset text-fg hover:brightness-[0.97] dark:hover:brightness-125",
        ghost: "text-fg-muted hover:bg-surface-inset hover:text-fg",
        danger: "bg-danger text-white shadow-[var(--shadow-lift)] hover:brightness-110",
        /**
         * A change worth pausing over, but nothing is lost by making it. The
         * palette's brightest amber rather than the semantic `--warning`, which
         * is tuned for text on a page and reads brown behind white.
         */
        warning:
          "bg-[var(--color-warning-400)] text-white shadow-[var(--shadow-lift)] hover:brightness-105",
        link: "text-accent underline-offset-4 hover:underline",
      },
      /**
       * The default is 32 tall, not 40. A screen here is a toolbar over a
       * table, and at 40 the actions took more of it than the rows did.
       *
       * `sm` is the same height and stays: at 32 the step between sizes is
       * padding and type, not height — `md` is the roomier of the two, and the
       * one a screen should reach for.
       */
      size: {
        xs: "h-7 px-2.5 text-footnote",
        sm: "h-8 px-3 text-subhead",
        md: "h-8 px-3.5 text-callout",
        lg: "h-11 px-5 text-body",
        xl: "h-12 px-6 text-body",
        icon: "size-8 px-0",
        /** Kept as the name a few screens already write; same thing as `icon`. */
        "icon-sm": "size-8 px-0",
        /**
         * Square, and exactly as tall as a `md` field — `controlHeight.md` is
         * `h-10`. For the button that stands beside an input rather than in a
         * row of buttons: at the default 32 it came up eight pixels short of
         * the control it belongs to, which reads as a misaligned button rather
         * than as a pair.
         */
        "icon-md": "size-10 px-0",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Swaps the label for a spinner without letting the button change width. */
  loading?: boolean;
  /** Announced while `loading`, so the change is not carried by the spinner alone. */
  loadingLabel?: ReactNode;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  loadingLabel,
  children,
  disabled,
  type,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      // A bare `<button>` inside a form submits it, which is never what an
      // icon button meant — revealing a password should not sign you in.
      // Submitting is opted into with `type="submit"`; every form here does.
      {...(asChild ? {} : { type: type ?? "button" })}
      className={cn(buttonVariants({ variant, size }), className)}
      aria-busy={loading || undefined}
      disabled={asChild ? undefined : disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          {/* The label stays in the flow but invisible, so the button keeps its
              width and the row it sits in does not shift while saving. */}
          <span className="invisible contents">{children}</span>
          <span className="absolute inset-0 grid place-items-center">
            <Spinner />
          </span>
          <span className="sr-only">{loadingLabel ?? "Working"}</span>
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

/**
 * A button whose meaning is its icon. The label is required and rendered for
 * screen readers, and doubles as the tooltip — an icon nobody can name is a
 * guess, not an action.
 */
export function IconButton({
  label,
  children,
  className,
  variant = "ghost",
  size = "icon",
  ...props
}: Omit<ButtonProps, "aria-label"> & { label: string }) {
  return (
    <Button
      title={label}
      aria-label={label}
      variant={variant}
      size={size}
      className={className}
      {...props}
    >
      {children}
    </Button>
  );
}

export { buttonVariants };
